// Polyfills must be imported first
import "./polyfills";

// Initialize Sentry before anything else
import { initSentry, captureError } from "./lib/sentry";
initSentry();

import { hasRecentReload } from "./lib/lazyWithRetry";

// Handle Vite modulepreload failures (stale chunks after deployment).
// This fires before React mounts, covering the gap that lazyWithRetry
// and SentryErrorBoundary can't reach.
window.addEventListener('vite:preloadError', (event) => {
  if (!hasRecentReload()) {
    event.preventDefault();
    try { sessionStorage.setItem('dextoro_chunk_reload', String(Date.now())); } catch { /* sessionStorage unavailable */ }
    // Clear CacheStorage before reload to evict any cached bad responses
    const clearAndReload = async () => {
      try {
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch { /* best-effort */ }
      window.location.reload();
    };
    clearAndReload();
  }
});

import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { TurnkeyProvider } from "./providers/TurnkeyProvider";
import { SentryErrorBoundary } from "./components/SentryErrorBoundary";
import { loadTurnkeyRuntimeConfig } from "./lib/turnkey/loadTurnkeyRuntimeConfig";
import { loadSolanaRuntimeConfig } from "./lib/solana/loadSolanaRuntimeConfig";
import { logSolanaConfig } from "./lib/solana/config";
import "./index.css";

// Fire config loaders as fire-and-forget (non-blocking).
// These configs are only needed when the user signs in (Turnkey) or makes RPC calls (Solana),
// not for initial page render.
Promise.all([
  loadTurnkeyRuntimeConfig(),
  loadSolanaRuntimeConfig(),
])
  .then(() => logSolanaConfig())
  .catch((err) => {
    console.warn("[bootstrap] Runtime config load error:", err);
    captureError(err instanceof Error ? err : new Error(String(err)), {
      feature: 'bootstrap_config',
    });
  });

// Render React immediately without waiting for configs
createRoot(document.getElementById("root")!).render(
  <SentryErrorBoundary>
    <TurnkeyProvider>
      <App />
    </TurnkeyProvider>
  </SentryErrorBoundary>
);

